// SignupAllowlistView — admin pré-cadastra emails que podem se cadastrar
// via /signup. Sem allowlist, signup público é negado.
//
// Funções:
//   - Adicionar 1+ emails de uma vez (textarea com lista)
//   - Opcional: vincular Company alvo + role default (vira membership ao
//     completar cadastro)
//   - Revogar convites pendentes (sem deletar — vira status='revoked')
//   - Ver histórico (pending / used / revoked) com data e by-whom

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  RefreshCw,
  Send,
  Trash2,
  UserPlus,
  XCircle
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { Switch } from "@app/ui/components/switch";
import {
  api,
  ApiError,
  call,
  type CompanyMin,
  type GroupListRow,
  type SignupAllowlistRow
} from "./api";
import { openAlert, openConfirm } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const phase = signal<"loading" | "ready" | "error">("loading");
const rows = signal<SignupAllowlistRow[]>([]);
const companies = signal<CompanyMin[]>([]);
const groupsList = signal<GroupListRow[]>([]);
const studyGroupsList = signal<
  Array<{
    id: string;
    name: string;
    description: string | null;
    memberCount: number;
    itemCount: number;
    createdByName: string | null;
    archivedAt: string | null;
  }>
>([]);
const errMsg = signal<string | null>(null);

const refreshing = signal(false);

async function load(opts: { silent?: boolean } = {}) {
  if (!opts.silent) phase.value = "loading";
  if (opts.silent) refreshing.value = true;
  errMsg.value = null;
  try {
    const [allow, users, gs, sgs] = await Promise.all([
      api.listSignupAllowlist(),
      api.listUsers(),
      api.listGroups(),
      api.listStudyGroups().catch(() => ({ groups: [] }))
    ]);
    rows.value = allow.items;
    companies.value = users.companies ?? [];
    groupsList.value = gs.groups;
    studyGroupsList.value = sgs.groups;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    if (!opts.silent) phase.value = "error";
  } finally {
    refreshing.value = false;
  }
}

const activeTab = signal<"invites" | "interests">("invites");

export function SignupAllowlistView() {
  useEffect(() => {
    void load();
  }, []);
  const list = rows.value;
  const pending = list.filter((r) => r.status === "pending");
  const used = list.filter((r) => r.status === "used");
  const revoked = list.filter((r) => r.status === "revoked");
  const tab = activeTab.value;

  if (phase.value === "loading") {
    return (
      <div class="space-y-2">
        <Skeleton class="h-24 w-full" />
        <Skeleton class="h-24 w-full" />
      </div>
    );
  }
  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );
  }

  return (
    <div class="space-y-4">
      <div class="flex items-center border-b border-border">
        <TabButton
          label="Convites"
          active={tab === "invites"}
          onClick={() => (activeTab.value = "invites")}
        />
        <TabButton
          label="Manifestações de interesse"
          active={tab === "interests"}
          onClick={() => (activeTab.value = "interests")}
        />
        <div class="ml-auto pb-1.5">
          <button
            type="button"
            onClick={() => {
              // Recarrega AS DUAS abas — convites + manifestações — pra
              // evitar o caso reportado em que clicar Atualizar não
              // atualizava a aba ativa (a manifestações tinha o próprio
              // loadInterests separado).
              void load({ silent: true });
              void loadInterests({ silent: true });
            }}
            disabled={refreshing.value}
            class="inline-flex items-center gap-1.5 rounded-md border border-border bg-card/60 px-3 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            title="Recarregar convites e manifestações"
            aria-label="Atualizar"
          >
            {refreshing.value ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <RefreshCw className="size-3.5" />
            )}
            Atualizar
          </button>
        </div>
      </div>

      {tab === "interests" && <InterestsPanel />}

      {tab === "invites" && (
        <>
      <div class="rounded-md border border-blue-500/30 bg-blue-500/5 px-4 py-3 text-sm">
        <Mail className="mr-1 inline size-4 text-blue-600" />
        <strong>Self-signup controlado.</strong> Adicione aqui os emails autorizados
        a criar conta. O signup público em <code class="rounded bg-muted px-1 text-xs">/signup</code>{" "}
        só funciona pra quem está nessa lista. Emails são deduplicados em formato
        Gmail (luiz.moraes@ ≡ luizmoraes@).
      </div>

      <InviteForm />

      <Section
        title={`Pendentes (${pending.length})`}
        items={pending}
        emptyMessage="Nenhum convite pendente."
      />
      {used.length > 0 && (
        <Section
          title={`Cadastrados (${used.length})`}
          items={used}
          dimmed
          emptyMessage=""
        />
      )}
      {revoked.length > 0 && (
        <Section
          title={`Revogados (${revoked.length})`}
          items={revoked}
          dimmed
          emptyMessage=""
        />
      )}
        </>
      )}
    </div>
  );
}

interface InterestRow {
  id: string;
  email: string;
  fullName: string;
  studyLocation: string;
  studyArea: string;
  interestMessage: string;
  status: "pending" | "approved" | "rejected";
  approvedAt: string | null;
  rejectedAt: string | null;
  rejectedReason: string | null;
  createdAt: string;
}

const interests = signal<InterestRow[]>([]);
const interestsPhase = signal<"idle" | "loading" | "ready" | "error">("idle");
const interestsErr = signal<string | null>(null);
const interestsFilter = signal<"pending" | "approved" | "rejected" | "all">("pending");
const selectedIds = signal<Set<string>>(new Set());

async function loadInterests(opts: { silent?: boolean } = {}) {
  if (!opts.silent) interestsPhase.value = "loading";
  interestsErr.value = null;
  try {
    const params = new URLSearchParams({ status: interestsFilter.value });
    const res = await call<{ interests: InterestRow[] }>(
      `/api/manager/signup-interests?${params.toString()}`
    );
    interests.value = res.interests;
    interestsPhase.value = "ready";
    // Limpa seleção quando troca de filtro.
    selectedIds.value = new Set();
  } catch (e) {
    interestsErr.value = friendlyError(e);
    if (!opts.silent) interestsPhase.value = "error";
  }
}

function InterestsPanel() {
  useEffect(() => {
    void loadInterests();
  }, [interestsFilter.value]);

  const filter = interestsFilter.value;
  const list = interests.value;
  const sel = selectedIds.value;
  const allSelected = list.length > 0 && list.every((r) => sel.has(r.id));

  function toggleAll() {
    if (allSelected) {
      selectedIds.value = new Set();
    } else {
      selectedIds.value = new Set(list.map((r) => r.id));
    }
  }

  function toggleOne(id: string) {
    const next = new Set(sel);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selectedIds.value = next;
  }

  async function approveSelected() {
    if (sel.size === 0) return;
    const ok = await openConfirm({
      title: `Aprovar ${sel.size} manifestação(ões)?`,
      message:
        "Pra cada uma vou: 1) inserir o email no allowlist, 2) enviar convite por email. Não dá pra desfazer em lote.",
      confirmLabel: "Aprovar e convidar"
    });
    if (!ok) return;
    try {
      const res = await call<{ approved: number; results: Array<{ email: string; emailSent: boolean; reason?: string }> }>(
        "/api/manager/signup-interests?action=approve",
        {
          method: "POST",
          body: JSON.stringify({ ids: Array.from(sel) })
        }
      );
      const failed = res.results.filter((r) => !r.emailSent);
      const msg =
        failed.length === 0
          ? `${res.approved} convite(s) enviado(s).`
          : `${res.approved - failed.length} OK, ${failed.length} falharam: ${failed.map((f) => `${f.email}${f.reason ? " (" + f.reason + ")" : ""}`).join(", ")}`;
      void openAlert({ title: "Aprovação processada", message: msg });
      void loadInterests();
      void load(); // refresh allowlist tab também
    } catch (e) {
      void openAlert({ title: "Erro ao aprovar", message: friendlyError(e) });
    }
  }

  async function rejectSelected() {
    if (sel.size === 0) return;
    const ok = await openConfirm({
      title: `Rejeitar ${sel.size} manifestação(ões)?`,
      message: "Vai marcar como rejected. Não envia email pra ninguém.",
      confirmLabel: "Rejeitar",
      destructive: true
    });
    if (!ok) return;
    try {
      await call("/api/manager/signup-interests?action=reject", {
        method: "POST",
        body: JSON.stringify({ ids: Array.from(sel) })
      });
      void loadInterests();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }

  return (
    <div class="space-y-3">
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-4 py-3 text-sm">
        <UserPlus className="mr-1 inline size-4 text-violet-600" />
        <strong>Lista de espera.</strong> Quem tenta criar conta e não tá no
        allowlist preenche o formulário público. Aprovar aqui = inserir no
        allowlist + enviar convite por email.
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <FilterChip label="Pendentes" value="pending" />
        <FilterChip label="Aprovadas" value="approved" />
        <FilterChip label="Rejeitadas" value="rejected" />
        <FilterChip label="Todas" value="all" />
        <div class="ml-auto flex items-center gap-2">
          {sel.size > 0 && (
            <span class="text-xs text-muted-foreground">
              {sel.size} selecionada(s)
            </span>
          )}
          <button
            type="button"
            onClick={() => void approveSelected()}
            disabled={sel.size === 0}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-40"
          >
            <Send className="size-3.5" />
            Aprovar + convidar
          </button>
          <button
            type="button"
            onClick={() => void rejectSelected()}
            disabled={sel.size === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-40"
          >
            <XCircle className="size-3.5" />
            Rejeitar
          </button>
        </div>
      </div>

      {interestsPhase.value === "loading" && (
        <p class="flex items-center gap-2 py-6 text-center text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando…
        </p>
      )}
      {interestsPhase.value === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="mr-2 inline size-4" />
          {interestsErr.value}
        </div>
      )}

      {interestsPhase.value === "ready" && list.length === 0 && (
        <div class="rounded-md border border-dashed border-border bg-background/40 px-4 py-10 text-center">
          <p class="text-sm text-muted-foreground">
            {filter === "pending"
              ? "Nenhuma manifestação pendente."
              : "Nada por aqui."}
          </p>
        </div>
      )}

      {interestsPhase.value === "ready" && list.length > 0 && (
        <div class="overflow-x-auto rounded-md border border-border">
          <table class="w-full text-sm">
            <thead class="bg-muted/40 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <tr>
                <th class="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Selecionar todos"
                  />
                </th>
                <th class="px-3 py-2 text-left">Nome / Email</th>
                <th class="px-3 py-2 text-left">Onde estuda</th>
                <th class="px-3 py-2 text-left">Área</th>
                <th class="px-3 py-2 text-left">Mensagem</th>
                <th class="px-3 py-2 text-left">Recebida</th>
                <th class="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <InterestTr
                  key={r.id}
                  row={r}
                  selected={sel.has(r.id)}
                  onToggle={() => toggleOne(r.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FilterChip({
  label,
  value
}: {
  label: string;
  value: "pending" | "approved" | "rejected" | "all";
}) {
  const active = interestsFilter.value === value;
  return (
    <button
      type="button"
      onClick={() => (interestsFilter.value = value)}
      class={`rounded-full border px-3 py-1 text-[11px] font-medium transition-colors ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}

function InterestTr({
  row,
  selected,
  onToggle
}: {
  row: InterestRow;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <tr class={`border-t border-border ${selected ? "bg-primary/5" : ""}`}>
      <td class="px-3 py-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Selecionar ${row.email}`}
          disabled={row.status !== "pending"}
        />
      </td>
      <td class="px-3 py-2">
        <div class="font-semibold">{row.fullName}</div>
        <div class="text-[11px] text-muted-foreground">{row.email}</div>
      </td>
      <td class="px-3 py-2 text-xs">{row.studyLocation || "—"}</td>
      <td class="px-3 py-2 text-xs">{row.studyArea || "—"}</td>
      <td class="px-3 py-2 max-w-md text-[11px] text-muted-foreground">
        <div class="line-clamp-3 whitespace-pre-wrap">
          {row.interestMessage || "—"}
        </div>
      </td>
      <td class="px-3 py-2 text-[11px] text-muted-foreground">
        {new Date(row.createdAt).toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric"
        })}
      </td>
      <td class="px-3 py-2">
        {row.status === "pending" && (
          <span class="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
            <Clock className="size-3" /> Pendente
          </span>
        )}
        {row.status === "approved" && (
          <span class="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="size-3" /> Aprovada
          </span>
        )}
        {row.status === "rejected" && (
          <span class="inline-flex items-center gap-1 rounded-md bg-destructive/15 px-2 py-0.5 text-[10px] font-semibold text-destructive">
            <XCircle className="size-3" /> Rejeitada
          </span>
        )}
      </td>
    </tr>
  );
}

function TabButton({
  label,
  active,
  onClick
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
        active
          ? "border-primary text-primary"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}

function InviteForm() {
  const [emailsRaw, setEmailsRaw] = useState("");
  const [targetCompanyId, setTargetCompanyId] = useState("");
  const [targetRole, setTargetRole] = useState<"" | "student" | "teacher" | "admin_company">(
    ""
  );
  const [targetGroupIds, setTargetGroupIds] = useState<string[]>([]);
  const [targetStudyGroupIds, setTargetStudyGroupIds] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [sendInvite, setSendInvite] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    added: number;
    skipped: { email: string; reason: string }[];
    emailsOk: number;
    emailsFailed: { email: string; reason?: string }[];
  } | null>(null);

  async function submit() {
    const list = emailsRaw
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (list.length === 0) return;
    setSubmitting(true);
    setResult(null);
    try {
      const r = await api.addSignupAllowlist({
        emails: list,
        targetCompanyId: targetCompanyId || null,
        targetRole: targetRole || null,
        targetGroupIds,
        targetStudyGroupIds,
        note: note || undefined,
        sendInvite
      });
      const emailResults = r.emailResults ?? [];
      setResult({
        added: r.added.length,
        skipped: r.skipped,
        emailsOk: emailResults.filter((e) => e.ok).length,
        emailsFailed: emailResults
          .filter((e) => !e.ok)
          .map((e) => ({ email: e.email, reason: e.reason }))
      });
      setEmailsRaw("");
      await load();
    } catch (e) {
      const m = friendlyError(e);
      setResult({
        added: 0,
        skipped: [{ email: "(erro)", reason: m }],
        emailsOk: 0,
        emailsFailed: []
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div class="rounded-md border border-border bg-card p-6">
      <h3 class="mb-3 text-sm font-bold uppercase tracking-wider text-muted-foreground">
        <UserPlus className="mr-1 inline size-4" />
        Adicionar convites
      </h3>
      <div class="space-y-3">
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Emails (um por linha, ou separados por vírgula/espaço)
          </label>
          <textarea
            value={emailsRaw}
            onInput={(e) => setEmailsRaw((e.target as HTMLTextAreaElement).value)}
            placeholder="aluno1@exemplo.com&#10;aluno2@exemplo.com"
            rows={4}
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            disabled={submitting}
          />
        </div>
        <div class="grid gap-3 md:grid-cols-3">
          <div>
            <label class="mb-1 block text-xs font-medium text-muted-foreground">
              Organização (opcional)
            </label>
            <select
              value={targetCompanyId}
              onChange={(e) =>
                setTargetCompanyId((e.target as HTMLSelectElement).value)
              }
              aria-label="Organização do convite"
              title="Organização do convite"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              disabled={submitting}
            >
              <option value="">— Sem vínculo (plano free) —</option>
              {companies.value.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-muted-foreground">
              Papel default
            </label>
            <select
              value={targetRole}
              onChange={(e) =>
                setTargetRole(
                  (e.target as HTMLSelectElement).value as
                    | ""
                    | "student"
                    | "teacher"
                    | "admin_company"
                )
              }
              aria-label="Papel default na organização"
              title="Papel default na organização"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              disabled={submitting || !targetCompanyId}
            >
              <option value="">— Sem papel —</option>
              <option value="student">Aluno</option>
              <option value="teacher">Professor</option>
              <option value="admin_company">Admin da organização</option>
            </select>
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-muted-foreground">
              Observação (opcional)
            </label>
            <input
              value={note}
              onInput={(e) => setNote((e.target as HTMLInputElement).value)}
              placeholder="Ex.: Turma 2026.1"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              disabled={submitting}
            />
          </div>
        </div>
        <GroupsMultiSelect
          selected={targetGroupIds}
          onChange={setTargetGroupIds}
          disabled={submitting}
        />
        <StudyGroupsMultiSelect
          selected={targetStudyGroupIds}
          onChange={setTargetStudyGroupIds}
          disabled={submitting}
        />
        <label class="flex items-center gap-2 rounded-md border border-border bg-background/50 px-3 py-2 text-xs">
          <Switch
            checked={sendInvite}
            onCheckedChange={(v) => setSendInvite(v)}
            disabled={submitting}
          />
          <Send className="size-3.5 text-primary" />
          <span>
            Enviar email de convite agora{" "}
            <span class="text-muted-foreground">
              (link pra criar conta com este email)
            </span>
          </span>
        </label>
        <div class="flex items-center justify-end gap-2">
          {result && (
            <div class="flex-1 space-y-0.5 text-xs">
              {result.added > 0 && (
                <div class="text-emerald-600">
                  <CheckCircle2 className="mr-1 inline size-3.5" />
                  {result.added} adicionado{result.added !== 1 ? "s" : ""}
                  {result.emailsOk > 0 && (
                    <span class="ml-2 text-emerald-700">
                      · {result.emailsOk} email{result.emailsOk !== 1 ? "s" : ""} enviado
                      {result.emailsOk !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              )}
              {result.emailsFailed.length > 0 && (
                <div class="text-red-600">
                  <AlertCircle className="mr-1 inline size-3.5" />
                  {result.emailsFailed.length} email
                  {result.emailsFailed.length !== 1 ? "s" : ""} falhou:{" "}
                  {result.emailsFailed
                    .slice(0, 2)
                    .map((s) => `${s.email}${s.reason ? ` (${s.reason})` : ""}`)
                    .join(", ")}
                </div>
              )}
              {result.skipped.length > 0 && (
                <div class="text-amber-600">
                  <AlertCircle className="mr-1 inline size-3.5" />
                  {result.skipped.length} ignorado{result.skipped.length !== 1 ? "s" : ""}:{" "}
                  {result.skipped
                    .slice(0, 3)
                    .map((s) => `${s.email} (${labelReason(s.reason)})`)
                    .join(", ")}
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !emailsRaw.trim()}
            class="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <UserPlus className="size-4" />
            )}
            Adicionar
          </button>
        </div>
      </div>
    </div>
  );
}

function labelReason(r: string): string {
  if (r === "invalid_format") return "formato inválido";
  if (r === "already_registered") return "já cadastrado";
  if (r === "already_invited") return "já convidado";
  return r;
}

function Section({
  title,
  items,
  emptyMessage,
  dimmed
}: {
  title: string;
  items: SignupAllowlistRow[];
  emptyMessage: string;
  dimmed?: boolean;
}) {
  return (
    <section>
      <h2 class="mb-2 text-sm font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h2>
      {items.length === 0 ? (
        emptyMessage ? (
          <div class="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
            {emptyMessage}
          </div>
        ) : null
      ) : (
        <ul
          class={`divide-y divide-border overflow-hidden rounded-md border border-border bg-card/40 ${
            dimmed ? "opacity-70" : ""
          }`}
        >
          {items.map((r) => (
            <InviteRow key={r.id} row={r} />
          ))}
        </ul>
      )}
    </section>
  );
}

function InviteRow({ row }: { row: SignupAllowlistRow }) {
  const [busy, setBusy] = useState<"revoke" | "resend" | null>(null);
  async function revoke() {
    const ok = await openConfirm({
      title: "Revogar convite?",
      message: `O email ${row.email} não poderá mais se cadastrar. Você pode adicioná-lo de novo depois se mudar de ideia.`,
      confirmLabel: "Revogar",
      danger: true
    });
    if (!ok) return;
    setBusy("revoke");
    try {
      await api.revokeSignupAllowlist(row.id);
      await load();
    } finally {
      setBusy(null);
    }
  }

  async function resend() {
    setBusy("resend");
    try {
      const r = await api.resendSignupAllowlist(row.id);
      if (r.ok) {
        await openAlert({
          title: "Email reenviado",
          message: `Convite reenviado pra ${row.email}.`
        });
      } else {
        await openAlert({
          title: "Falha ao enviar",
          message:
            r.reason ??
            "Não foi possível enviar o email. Verifique a configuração do Resend no painel."
        });
      }
    } catch (e) {
      await openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <li class="flex items-center gap-3 px-4 py-3 text-sm">
      <StatusIcon status={row.status} />
      <div class="min-w-0 flex-1">
        <div class="truncate font-medium">{row.email}</div>
        <div class="truncate text-xs text-muted-foreground">
          {row.targetCompanyName && (
            <span class="mr-2">
              {row.targetCompanyName}
              {row.targetRole && ` · ${labelRole(row.targetRole)}`}
            </span>
          )}
          {row.note && <span class="mr-2">· {row.note}</span>}
          <span class="mr-2">
            Convidado em {new Date(row.invitedAt).toLocaleDateString("pt-BR")}
          </span>
          {row.usedAt && (
            <span class="text-emerald-600">
              · usado em {new Date(row.usedAt).toLocaleDateString("pt-BR")}
            </span>
          )}
        </div>
      </div>
      {row.status === "pending" && (
        <div class="flex items-center gap-1">
          <button
            type="button"
            onClick={resend}
            disabled={busy !== null}
            title="Reenviar email de convite"
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:border-primary hover:text-primary disabled:opacity-50"
          >
            {busy === "resend" ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <Send className="size-3" />
            )}
            Reenviar
          </button>
          <button
            type="button"
            onClick={revoke}
            disabled={busy !== null}
            title="Revogar convite"
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:border-destructive hover:text-destructive disabled:opacity-50"
          >
            {busy === "revoke" ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <Trash2 className="size-3" />
            )}
            Revogar
          </button>
        </div>
      )}
    </li>
  );
}

function StatusIcon({ status }: { status: "pending" | "used" | "revoked" }) {
  if (status === "used") return <CheckCircle2 className="size-4 text-emerald-600" />;
  if (status === "revoked") return <XCircle className="size-4 text-muted-foreground" />;
  return <Clock className="size-4 text-amber-500" />;
}

function labelRole(r: "admin_company" | "teacher" | "student"): string {
  if (r === "admin_company") return "Admin da org.";
  if (r === "teacher") return "Professor";
  return "Aluno";
}

/** Multi-select de Turmas (groups) com check-toggle inline. Quando o user
 *  completa o signup, ele é matriculado nessas turmas automaticamente e os
 *  grants pros bancos são materializados. */
function GroupsMultiSelect({
  selected,
  onChange,
  disabled
}: {
  selected: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const all = groupsList.value;
  const selSet = new Set(selected);
  const visible = q.trim()
    ? all.filter((g) =>
        `${g.name} ${g.description ?? ""} ${g.companyName ?? ""}`
          .toLowerCase()
          .includes(q.trim().toLowerCase())
      )
    : all.slice(0, 50);

  function toggle(id: string) {
    if (selSet.has(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  }

  const selectedNames = selected
    .map((id) => all.find((g) => g.id === id)?.name)
    .filter(Boolean) as string[];

  return (
    <div class="rounded-md border border-border bg-background p-3 text-xs">
      <div class="mb-1.5 flex items-center justify-between">
        <label class="font-medium text-muted-foreground">
          Matricular em turmas (opcional)
        </label>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          class="rounded-md border border-border bg-background px-2 py-1 text-[10px] font-semibold hover:bg-accent disabled:opacity-50"
        >
          {open ? "Fechar" : selected.length > 0 ? "Editar" : "Escolher turmas"}
        </button>
      </div>
      {selected.length > 0 && !open && (
        <div class="flex flex-wrap gap-1">
          {selectedNames.map((n) => (
            <span
              key={n}
              class="inline-flex items-center gap-1 rounded bg-sky-500/10 px-2 py-0.5 text-[10px] font-semibold text-sky-700 ring-1 ring-sky-500/20 dark:text-sky-300"
            >
              {n}
            </span>
          ))}
        </div>
      )}
      {selected.length === 0 && !open && (
        <p class="text-[10px] italic text-muted-foreground">
          Quando o user aceitar o convite, ele entra direto nas turmas escolhidas
          e ganha acesso aos bancos delas.
        </p>
      )}
      {open && (
        <div class="space-y-2">
          <input
            type="search"
            value={q}
            onInput={(e) => setQ((e.target as HTMLInputElement).value)}
            placeholder="Buscar turma…"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-xs"
            disabled={disabled}
          />
          <div class="max-h-[200px] overflow-y-auto rounded-md border border-border bg-background">
            {visible.length === 0 ? (
              <p class="p-3 text-center text-[11px] italic text-muted-foreground">
                Nenhuma turma encontrada.
              </p>
            ) : (
              <ul class="divide-y divide-border">
                {visible.map((g) => {
                  const checked = selSet.has(g.id);
                  return (
                    <li key={g.id}>
                      <button
                        type="button"
                        onClick={() => toggle(g.id)}
                        disabled={disabled}
                        class={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors disabled:opacity-50 ${
                          checked ? "bg-primary/10" : "hover:bg-accent/30"
                        }`}
                      >
                        <span
                          class={`inline-flex size-3.5 shrink-0 items-center justify-center rounded border ${
                            checked
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border bg-background"
                          }`}
                        >
                          {checked && <CheckCircle2 className="size-2.5" />}
                        </span>
                        <span class="min-w-0 flex-1">
                          <span class="block truncate font-semibold">{g.name}</span>
                          <span class="block truncate text-[10px] text-muted-foreground">
                            {g.companyName ?? "Plataforma"} · {g.memberCount} membro{g.memberCount !== 1 ? "s" : ""} · {g.bankCount} banco{g.bankCount !== 1 ? "s" : ""}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Multi-select de Grupos de Estudo (peer-led) — análogo a GroupsMultiSelect
 *  mas pra study_groups. Aluno é matriculado direto como `member` ao concluir
 *  o signup. [[project-study-groups]] */
function StudyGroupsMultiSelect({
  selected,
  onChange,
  disabled
}: {
  selected: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const all = studyGroupsList.value;
  const selSet = new Set(selected);
  const visible = q.trim()
    ? all.filter((g) =>
        `${g.name} ${g.description ?? ""} ${g.createdByName ?? ""}`
          .toLowerCase()
          .includes(q.trim().toLowerCase())
      )
    : all.slice(0, 50);

  function toggle(id: string) {
    if (selSet.has(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  }

  const selectedNames = selected
    .map((id) => all.find((g) => g.id === id)?.name)
    .filter(Boolean) as string[];

  return (
    <div class="rounded-md border border-violet-500/30 bg-violet-500/5 p-3 text-xs">
      <div class="mb-1.5 flex items-center justify-between">
        <label class="font-medium text-violet-700 dark:text-violet-300">
          Matricular em Grupos de Estudo (opcional)
        </label>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          class="rounded-md border border-border bg-background px-2 py-1 text-[10px] font-semibold hover:bg-accent disabled:opacity-50"
        >
          {open ? "Fechar" : selected.length > 0 ? "Editar" : "Escolher grupos"}
        </button>
      </div>
      {selected.length > 0 && !open && (
        <div class="flex flex-wrap gap-1">
          {selectedNames.map((n) => (
            <span
              key={n}
              class="inline-flex items-center gap-1 rounded bg-violet-500/15 px-2 py-0.5 text-[10px] font-semibold text-violet-700 ring-1 ring-violet-500/30 dark:text-violet-300"
            >
              {n}
            </span>
          ))}
        </div>
      )}
      {selected.length === 0 && !open && (
        <p class="text-[10px] italic text-muted-foreground">
          Aluno entra direto como membro nos grupos escolhidos e ganha acesso
          aos bancos compartilhados.
        </p>
      )}
      {open && (
        <div class="space-y-2">
          <input
            type="search"
            value={q}
            onInput={(e) => setQ((e.target as HTMLInputElement).value)}
            placeholder="Buscar grupo de estudo…"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-xs"
            disabled={disabled}
          />
          <div class="max-h-[200px] overflow-y-auto rounded-md border border-border bg-background">
            {visible.length === 0 ? (
              <p class="p-3 text-center text-[11px] italic text-muted-foreground">
                {all.length === 0
                  ? "Nenhum Grupo de Estudo cadastrado ainda."
                  : "Nenhum grupo encontrado."}
              </p>
            ) : (
              <ul class="divide-y divide-border">
                {visible.map((g) => {
                  const checked = selSet.has(g.id);
                  return (
                    <li key={g.id}>
                      <button
                        type="button"
                        onClick={() => toggle(g.id)}
                        disabled={disabled}
                        class={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors disabled:opacity-50 ${
                          checked ? "bg-violet-500/10" : "hover:bg-accent/30"
                        }`}
                      >
                        <span
                          class={`inline-flex size-3.5 shrink-0 items-center justify-center rounded border ${
                            checked
                              ? "border-violet-500 bg-violet-500 text-white"
                              : "border-border bg-background"
                          }`}
                        >
                          {checked && (
                            <svg
                              viewBox="0 0 16 16"
                              fill="none"
                              class="size-2.5"
                            >
                              <path
                                d="M3 8.5L6.5 12L13 4.5"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          )}
                        </span>
                        <span class="min-w-0 flex-1">
                          <span class="block truncate font-medium">
                            {g.name}
                          </span>
                          <span class="block truncate text-[10px] text-muted-foreground">
                            {g.memberCount}{" "}
                            {g.memberCount === 1 ? "membro" : "membros"} ·{" "}
                            {g.itemCount}{" "}
                            {g.itemCount === 1 ? "banco" : "bancos"}
                            {g.createdByName && ` · ${g.createdByName}`}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
